<!-- UserCardModal.vue -->

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { useDashboardStore } from "@/stores/useDashboardStore";
import * as subscriptionService from "@/services/subscriptionService";
import GrantCourtesyModal from "@/components/User/GrantCourtesyModal.vue";

import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, default: false },
  userId: { type: Number, required: true },
});

const emit = defineEmits(["close"]);

const userStore = useUserStore();
const dashboardStore = useDashboardStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

// ── Cortesia ──────────────────────────────────────────────
const showCourtesyModal = ref(false);
const hasActiveCourtesy = ref(false);

async function fetchCourtesyStatus(targetUserId) {
  hasActiveCourtesy.value = false;
  try {
    const res = await subscriptionService.fetchSubscriptionsByUserId(
      targetUserId
    );
    const subs = Array.isArray(res.data) ? res.data : [];
    hasActiveCourtesy.value = subs.some(
      (s) =>
        s.status?.toLowerCase() === "active" &&
        s.paymentMethod?.toLowerCase() === "courtesy"
    );
  } catch {
    /* silencioso */
  }
}

const isSysAdmin = computed(
  () => userStore.currentUser?.userName?.toLowerCase() === "sys"
);

// ── Dados do usuário ───────────────────────────────────────
const isLoading = ref(false);
const userDetails = ref(null);
const fetchError = ref(null);

const statusLabel = computed(() => {
  switch (userDetails.value?.status) {
    case 0:
      return { text: "Pendente", class: "status-pending" };
    case 1:
      return { text: "Ativo", class: "status-active" };
    case 2:
      return { text: "Inativo", class: "status-inactive" };
    case 3:
      return { text: "Bloqueado", class: "status-blocked" };
    default:
      return { text: "Desconhecido", class: "" };
  }
});

const formattedBirthDate = computed(() => {
  if (userDetails.value?.birthDate) {
    const date = new Date(userDetails.value.birthDate);
    if (!isNaN(date)) return date.toLocaleDateString("pt-BR");
  }
  return "Não informado";
});

// ── Telefone ───────────────────────────────────────────────
const formatE164Phone = (ddi, phoneE164) => {
  if (!phoneE164 || !String(phoneE164).trim()) return "Não informado";
  const rawNumber = String(phoneE164).replace(/\D/g, "");
  if (rawNumber.length >= 10) {
    const ddiPrefix = ddi ? String(ddi).replace(/\D/g, "") : "";
    if (rawNumber.startsWith(ddiPrefix) && ddiPrefix) return `+${rawNumber}`;
    if (ddiPrefix && rawNumber.length < 13) return `+${ddiPrefix}${rawNumber}`;
    return `+${rawNumber}`;
  }
  return phoneE164;
};

const formattedPhoneNumber = computed(() => {
  const { isWhatsappPublic, ddi, phoneE164 } = userDetails.value ?? {};
  return isWhatsappPublic ? formatE164Phone(ddi, phoneE164) : "Não informado";
});

const formattedWhatsappNumber = computed(() => {
  const { ddi, phoneE164, isWhatsappPublic } = userDetails.value ?? {};
  if (!isWhatsappPublic || !phoneE164 || !String(phoneE164).trim())
    return "Não informado";
  return formatE164Phone(ddi, phoneE164);
});

// ── Foto ───────────────────────────────────────────────────
function getPhotoUrl(user) {
  let path = user.photoPath || user.photoUrl || "";
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  let base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  if (!path.startsWith("/")) path = "/" + path;
  return base + path;
}

// ── Buscar detalhes ────────────────────────────────────────
async function fetchUserDetails(id) {
  if (!id || id <= 0) {
    fetchError.value = "ID de usuário inválido.";
    return;
  }
  userDetails.value = null;
  fetchError.value = null;
  isLoading.value = true;
  try {
    userDetails.value = await userStore.getUserById(id);
  } catch (err) {
    fetchError.value = err.message || "Falha ao carregar os dados.";
  } finally {
    isLoading.value = false;
  }
}

// ── Follow ─────────────────────────────────────────────────
const isFollowing = ref(false);
const processingFollow = ref(false);
const followError = ref(null);

const isSelf = computed(() => userStore.currentUser?.userId === props.userId);

const isTargetSys = computed(
  () => userDetails.value?.userName?.toLowerCase() === "sys"
);

const canShowFollowButton = computed(() => {
  return !isSelf.value && !isTargetSys.value && userDetails.value?.status === 1;
});

async function fetchFollowState(targetUserId) {
  const currentUserId = userStore.currentUser?.userId;
  if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
    isFollowing.value = false;
    return;
  }
  try {
    const res = await api.get("/Follow/following");
    const list = Array.isArray(res.data) ? res.data : [];
    isFollowing.value = list.some((u) => u.userId === targetUserId);

    // Esta resposta é a mesma lista que o NoteCreatorsStack consome; aproveita
    // para semear o store em vez de deixá-lo refazer a chamada.
    userStore.followedUserIds = new Set(
      list
        .map((u) => u.userId)
        .filter((userId) => userId !== null && userId !== undefined)
    );
    userStore.hasLoadedFollowedUserIds = true;
  } catch {
    isFollowing.value = false;
  }
}

async function handleToggleFollow() {
  if (!userDetails.value || processingFollow.value || isSelf.value) return;
  const targetId = userDetails.value.userId;
  followError.value = null;
  processingFollow.value = true;
  try {
    if (isFollowing.value) {
      await api.delete(`/Follow/${targetId}`);
      isFollowing.value = false;
    } else {
      await api.post(`/Follow/${targetId}`);
      isFollowing.value = true;
    }
    // Mantém a pilha de criadores (NoteCreatorsStack) coerente na hora: este
    // card é alcançável de dentro da área de leitura, com a pilha na tela.
    userStore.setFollowedUser(targetId, isFollowing.value);
    await dashboardStore.fetchDashboard();
  } catch (err) {
    followError.value =
      err?.response?.data?.message ||
      err?.message ||
      "Não foi possível atualizar o seguimento deste usuário.";
  } finally {
    processingFollow.value = false;
  }
}

onMounted(() => {
  fetchUserDetails(props.userId);
  fetchFollowState(props.userId);
  fetchCourtesyStatus(props.userId);
});

function closeModal() {
  emit("close");
}

// ── Redes sociais ──────────────────────────────────────────
function sanitizeSocialValue(raw) {
  if (!raw) return "";
  let v = String(raw)
    .trim()
    .replace(/\s+/g, "")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/^instagram\.com\//i, "")
    .replace(/^facebook\.com\//i, "")
    .replace(/^linkedin\.com\/in\//i, "")
    .replace(/^linkedin\.com\//i, "")
    .replace(/^@/, "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  return v.trim();
}

function buildInstagramUrl(raw) {
  const h = sanitizeSocialValue(raw);
  return h ? `https://www.instagram.com/${h}/` : "";
}
function buildLinkedInUrl(raw) {
  const s = sanitizeSocialValue(raw);
  return s ? `https://www.linkedin.com/in/${s}/` : "";
}
function buildFacebookUrl(raw) {
  const p = sanitizeSocialValue(raw);
  return p ? `https://www.facebook.com/${p}` : "";
}

const instagramUrl = computed(() =>
  buildInstagramUrl(userDetails.value?.instagramUsername)
);
const linkedInUrl = computed(() =>
  buildLinkedInUrl(userDetails.value?.linkedInUsername)
);
const facebookUrl = computed(() =>
  buildFacebookUrl(userDetails.value?.facebookUsername)
);

// ── Copiar ─────────────────────────────────────────────────
const copiedHint = ref({ instagram: false, linkedin: false, facebook: false });
let copiedHintTimeout = null;

function showCopiedHint(key) {
  copiedHint.value.instagram =
    copiedHint.value.linkedin =
    copiedHint.value.facebook =
      false;
  if (copiedHintTimeout) clearTimeout(copiedHintTimeout);
  copiedHint.value[key] = true;
  copiedHintTimeout = setTimeout(() => {
    copiedHint.value[key] = false;
  }, 1200);
}

async function copyUrl(url, key) {
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    showCopiedHint(key);
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.cssText = "position:absolute;left:-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      showCopiedHint(key);
    } catch (err) {
      console.error("Falha ao copiar URL:", err);
    }
  }
}

// ── Bio ────────────────────────────────────────────────────
const isBioExpanded = ref(false);
const fullBio = computed(() =>
  String(userDetails.value?.bio || "Sem descrição.")
);
const bioTooLong = computed(() => fullBio.value.length > 200);
const bioPreview = computed(() => fullBio.value.slice(0, 200));
const displayedBio = computed(() => {
  if (!bioTooLong.value) return fullBio.value;
  return isBioExpanded.value ? fullBio.value : `${bioPreview.value}...`;
});
function toggleBio() {
  if (bioTooLong.value) isBioExpanded.value = !isBioExpanded.value;
}
</script>

<template>
  <Transition name="modal-fade">
    <div
      v-if="show"
      class="modal-overlay"
      @click.self="closeModal"
      @keydown.esc="closeModal"
      tabindex="-1"
    >
      <div
        ref="panelRef"
        class="user-card-content"
        role="dialog"
        aria-modal="true"
        :style="panelStyle"
      >
        <!-- HEADER -->
        <div
          class="modal-header"
          :class="{
 'modal-header--draggable': isDraggable,
            'modal-header--dragging': isDragging,
          }"
          @pointerdown="onHandlePointerDown"
        >
          <h3 class="card-title">Detalhes do Usuário</h3>
          <button
            @click="closeModal"
            type="button"
            class="modal-close-btn"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <!-- LOADING / ERROR -->
        <div v-if="isLoading" class="card-body loading-state">
          <i class="fas fa-spinner fa-spin"></i> Carregando...
        </div>
        <div v-else-if="fetchError" class="card-body error-state">
          <i class="fas fa-exclamation-triangle"></i> {{ fetchError }}
        </div>

        <!-- BODY -->
        <div v-else-if="userDetails" class="card-body">
          <!-- ═══ PROFILE HERO: foto à esquerda, info à direita ═══ -->
          <div class="profile-hero">
            <!-- AVATAR -->
            <div class="avatar-col">
              <div
                v-if="getPhotoUrl(userDetails)"
                class="profile-photo"
                :style="{
                  backgroundImage: `url('${getPhotoUrl(userDetails)}')`,
                }"
              />
              <div v-else class="profile-photo profile-photo--placeholder">
                <i class="fas fa-user"></i>
              </div>
              <!-- Badge de cortesia sobreposto ao avatar -->
              <span
                v-if="hasActiveCourtesy"
                class="courtesy-avatar-badge"
                title="Assinatura cortesia ativa"
                >🎁</span
              >
            </div>

            <!-- INFO + CHIPS -->
            <div class="info-col">
              <h4 class="profile-name">
                {{
                  userDetails.fullName ||
                  userDetails.userName ||
                  "Usuário Desconhecido"
                }}
              </h4>
              <span class="username"
                >@{{ userDetails.userName || "não informado" }}</span
              >

              <!-- Linha de badges -->
              <div class="badges-row">
                <span
                  v-if="userDetails.status !== undefined"
                  :class="['status-badge', statusLabel.class]"
                >
                  {{ statusLabel.text }}
                </span>
              </div>

              <!-- Chips de ação -->
              <div class="action-chips">
                <!-- Seguir / Seguindo -->
                <button
                  v-if="canShowFollowButton"
                  type="button"
                  class="chip chip--follow"
                  :class="
 isFollowing ? 'chip--following' : 'chip--not-following'
                  "
                  @click="handleToggleFollow"
                  :disabled="processingFollow"
                >
                  {{ isFollowing ? "Seguindo" : "Seguir" }}
                </button>

                <!-- Conceder Cortesia -->
                <button
                  v-if="isSysAdmin"
                  type="button"
                  class="chip chip--courtesy"
                  @click="showCourtesyModal = true"
                  title="Conceder acesso cortesia"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="chip-icon"
                  >
                    <path
                      d="M10 2a1 1 0 00-1 1v1H6a2 2 0 00-2 2v1H3a1 1 0 000 2h1v7a2 2 0 002 2h8a2 2 0 002-2V9h1a1 1 0 100-2h-1V6a2 2 0 00-2-2h-3V3a1 1 0 00-1-1zM7 6h6v1H7V6zM5 9h10v7H5V9z"
                    />
                  </svg>
                  Cortesia
                </button>
              </div>

              <!-- Erro de follow -->
              <p v-if="followError" class="follow-error">{{ followError }}</p>
            </div>
          </div>
          <!-- ═══ fim PROFILE HERO ═══ -->

          <!-- GRID DE DETALHES -->
          <div class="details-grid">
            <div class="detail-item bordered">
              <span class="detail-label">Nascimento:</span>
              <span class="detail-value">{{ formattedBirthDate }}</span>
            </div>
            <div class="detail-item bordered">
              <span class="detail-label">País:</span>
              <span class="detail-value">{{
                userDetails.country || "N/A"
              }}</span>
            </div>
            <div class="detail-item bordered">
              <span class="detail-label">Telefone:</span>
              <span class="detail-value">{{ formattedPhoneNumber }}</span>
            </div>
            <div class="detail-item bordered">
              <span class="detail-label">Profissão:</span>
              <span class="detail-value">{{
                userDetails.profession || "Não informada"
              }}</span>
            </div>

            <!-- Instagram -->
            <div class="detail-item bordered">
              <span class="detail-label instagram-label"
                ><i class="fab fa-instagram"></i> Instagram</span
              >
              <div class="url-row">
                <span
                  v-if="instagramUrl"
                  class="detail-value url-text"
                  :title="instagramUrl"
                  >{{ instagramUrl }}</span
                >
                <span v-else class="detail-value">Não informado</span>
                <div class="copy-wrap">
                  <button
                    v-if="instagramUrl"
                    type="button"
                    class="copy-btn"
                    @click="copyUrl(instagramUrl, 'instagram')"
                    title="Copiar URL"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      class="copy-icon"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 16c-1.105 0-2-.895-2-2V6c0-1.105.895-2 2-2h8c1.105 0 2 .895 2 2v8c0 1.105-.895 2-2 2H8z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 8h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-1"
                      />
                    </svg>
                  </button>
                  <span v-if="copiedHint.instagram" class="copied-hint"
                    >URL copiada</span
                  >
                </div>
              </div>
            </div>

            <!-- LinkedIn -->
            <div class="detail-item bordered">
              <span class="detail-label linkedin-label"
                ><i class="fab fa-linkedin-in"></i> LinkedIn</span
              >
              <div class="url-row">
                <span
                  v-if="linkedInUrl"
                  class="detail-value url-text"
                  :title="linkedInUrl"
                  >{{ linkedInUrl }}</span
                >
                <span v-else class="detail-value">Não informado</span>
                <div class="copy-wrap">
                  <button
                    v-if="linkedInUrl"
                    type="button"
                    class="copy-btn"
                    @click="copyUrl(linkedInUrl, 'linkedin')"
                    title="Copiar URL"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      class="copy-icon"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 16c-1.105 0-2-.895-2-2V6c0-1.105.895-2 2-2h8c1.105 0 2 .895 2 2v8c0 1.105-.895 2-2 2H8z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 8h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-1"
                      />
                    </svg>
                  </button>
                  <span v-if="copiedHint.linkedin" class="copied-hint"
                    >URL copiada</span
                  >
                </div>
              </div>
            </div>

            <!-- Facebook -->
            <div class="detail-item bordered">
              <span class="detail-label facebook-label"
                ><i class="fab fa-facebook-f"></i> Facebook</span
              >
              <div class="url-row">
                <span
                  v-if="facebookUrl"
                  class="detail-value url-text"
                  :title="facebookUrl"
                  >{{ facebookUrl }}</span
                >
                <span v-else class="detail-value">Não informado</span>
                <div class="copy-wrap">
                  <button
                    v-if="facebookUrl"
                    type="button"
                    class="copy-btn"
                    @click="copyUrl(facebookUrl, 'facebook')"
                    title="Copiar URL"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      class="copy-icon"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 16c-1.105 0-2-.895-2-2V6c0-1.105.895-2 2-2h8c1.105 0 2 .895 2 2v8c0 1.105-.895 2-2 2H8z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M16 8h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-1"
                      />
                    </svg>
                  </button>
                  <span v-if="copiedHint.facebook" class="copied-hint"
                    >URL copiada</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- WHATSAPP + TELEGRAM -->
          <div class="contacts-display-section">
            <div class="contact-display-item bordered">
              <span class="detail-label whatsapp-label"
                ><i class="fab fa-whatsapp"></i> WhatsApp</span
              >
              <span class="detail-value contact-number">{{
                formattedWhatsappNumber
              }}</span>
            </div>
            <div class="contact-display-item bordered">
              <span class="detail-label telegram-label"
                ><i class="fab fa-telegram-plane"></i> Telegram</span
              >
              <span
                v-if="userDetails.telegramUsername"
                class="detail-value contact-number"
              >
                @{{ userDetails.telegramUsername.replace(/^@/, "") }}
              </span>
              <span v-else class="detail-value contact-number"
                >Não informado</span
              >
            </div>
          </div>

          <!-- BIO -->
          <div class="bio-section">
            <span class="detail-label">Bio:</span>
            <p class="bio-text">
              {{ displayedBio }}
              <button
                v-if="bioTooLong"
                type="button"
                class="bio-more-link"
                @click="toggleBio"
              >
                {{ isBioExpanded ? "ver menos" : "ver mais" }}
              </button>
            </p>
          </div>

          <!-- LINKS CLICÁVEIS -->
          <div class="contacts-section">
            <template v-if="userDetails.status === 1">
              <a
                v-if="userDetails.isWhatsappPublic && userDetails.phoneE164"
                :href="`https://wa.me/${String(userDetails.phoneE164).replace(
                  /\D/g,
                  ''
                )}`"
                target="_blank"
                class="contact-link whatsapp"
              >
                <i class="fab fa-whatsapp"></i> WhatsApp
              </a>
              <a
                v-if="userDetails.telegramUsername?.trim()"
                :href="`https://t.me/${userDetails.telegramUsername
                  .replace(/^@/, '')
                  .trim()}`"
                target="_blank"
                class="contact-link telegram"
              >
                <i class="fab fa-telegram-plane"></i> Telegram
              </a>
              <a
                v-if="userDetails.instagramUsername?.trim()"
                :href="buildInstagramUrl(userDetails.instagramUsername)"
                target="_blank"
                class="contact-link instagram"
              >
                <i class="fab fa-instagram"></i> Instagram
              </a>
              <a
                v-if="userDetails.linkedInUsername?.trim()"
                :href="buildLinkedInUrl(userDetails.linkedInUsername)"
                target="_blank"
                class="contact-link linkedin"
              >
                <i class="fab fa-linkedin-in"></i> LinkedIn
              </a>
              <a
                v-if="userDetails.facebookUsername?.trim()"
                :href="buildFacebookUrl(userDetails.facebookUsername)"
                target="_blank"
                class="contact-link facebook"
              >
                <i class="fab fa-facebook-f"></i> Facebook
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Modal de cortesia -->
  <GrantCourtesyModal
    :show="showCourtesyModal"
    :userId="userId"
    :userName="userDetails?.fullName || userDetails?.userName || ''"
    @close="showCourtesyModal = false"
    @refresh="
      fetchUserDetails(userId);
      fetchCourtesyStatus(userId);
    "
  />
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-overlay {
  width: 100vw;
  height: 100vh;
}

.user-card-content {
  background-color: var(--surface);
  border-radius: 12px;
  width: 92%;
  max-width: 560px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
}

.card-body {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  /* remove o text-align: center global — o hero cuida do próprio alinhamento */
  text-align: left;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* ═══ PROFILE HERO ═══ */
.profile-hero {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: 0.875rem;
  margin-bottom: 0.875rem;
  border-bottom: 1px solid var(--surface-3);
}

/* Coluna do avatar — largura fixa */
.avatar-col {
  position: relative;
  flex-shrink: 0;
}

.profile-photo {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  border: 3px solid var(--surface-3);
  box-shadow: 0 0 0 2px var(--border-color);
}
.profile-photo--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--neutral-200);
  color: var(--text-subtle);
  font-size: 1.75rem;
}

/* Badge 🎁 sobre o avatar, canto inferior-direito */
.courtesy-avatar-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  background: var(--surface);
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  border: 1.5px solid #e9d5ff;
  line-height: 1;
}

/* Coluna de info — cresce */
.info-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.profile-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.username {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-subtle);
}

/* Linha de badges de status */
.badges-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.1rem;
}

.status-badge {
  font-size: 0.68rem;
  padding: 0.1rem 0.45rem;
  border-radius: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.4;
}
.status-active {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.status-pending {
  background-color: var(--warning-bg);
  color: #854d0e;
}
.status-inactive {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.status-blocked {
  background-color: #e9d5ff;
  color: #6b21a8;
}

/* Chips de ação */
.action-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.45rem;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.28rem 0.75rem;
  border-radius: 999px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.18s ease-in-out;
  white-space: nowrap;
}
.chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.chip:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
}

.chip--following {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border-color: #c4b5fd;
}
.chip--following:hover:not(:disabled) {
  background-color: var(--brand-soft-hover);
}
.chip--not-following {
  background-color: var(--surface-3);
  color: var(--text-dark);
  border-color: var(--neutral-400);
}
.chip--not-following:hover:not(:disabled) {
  background-color: var(--neutral-200);
}

.chip--courtesy {
  background-color: var(--brand-soft);
  color: var(--text-on-brand-soft);
  border-color: #c4b5fd;
}
.chip--courtesy:hover:not(:disabled) {
  background-color: #ddd6fe;
}

.chip-icon {
  width: 0.8rem;
  height: 0.8rem;
  flex-shrink: 0;
}

/* Erro de follow */
.follow-error {
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--danger-text);
}

/* GRID DE DETALHES */
.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  margin-bottom: 12px;
}
.detail-item {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
  min-width: 0;
}
.detail-item.bordered {
  border-bottom: 1px solid var(--border-color);
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.detail-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-dark);
  word-break: break-word;
}

.whatsapp-label i {
  color: #25d366;
  margin-right: 5px;
}
.telegram-label i {
  color: #0088cc;
  margin-right: 5px;
}
.instagram-label i {
  color: #e1306c;
  margin-right: 5px;
}
.linkedin-label i {
  color: #0a66c2;
  margin-right: 5px;
}
.facebook-label i {
  color: #1877f2;
  margin-right: 5px;
}

.url-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.url-text {
  flex: 1;
  min-width: 0;
  color: var(--text-dark);
  user-select: text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.copy-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.copy-btn {
  flex: 0 0 auto;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 8px;
  color: var(--text-muted);
  transition: background-color 0.2s, color 0.2s;
}
.copy-btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
  color: var(--text-dark);
}
.copy-icon {
  width: 16px;
  height: 16px;
}

.copied-hint {
  position: absolute;
  top: -28px;
  right: 0;
  background: rgba(15, 23, 42, 0.92);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 10px;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
}
.copied-hint::after {
  content: "";
  position: absolute;
  bottom: -6px;
  right: 10px;
  border-width: 6px 6px 0 6px;
  border-style: solid;
  border-color: rgba(15, 23, 42, 0.92) transparent transparent transparent;
}

.contacts-display-section {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
  padding: 8px 0;
}
.contact-display-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.contact-display-item.bordered {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 10px;
}
.contact-number {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-dark);
  word-break: break-all;
}

.bio-section {
  margin-bottom: 6px;
}
.bio-text {
  margin-top: 6px;
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.35;
  white-space: pre-wrap;
  text-align: justify;
  text-justify: inter-word;
}
.bio-more-link {
  margin-left: 6px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #0a66c2;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: underline;
}
.bio-more-link:hover {
  opacity: 0.85;
}

.contacts-section {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-color);
  flex-wrap: nowrap;
}
.contact-link {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  font-weight: 700;
  text-decoration: none;
  font-size: 0.8rem;
  line-height: 1;
  white-space: nowrap;
}
:deep(.contact-link i) {
  margin-right: 6px;
}

.whatsapp {
  background-color: #25d366;
  color: white;
}
.telegram {
  background-color: #0088cc;
  color: white;
}
.instagram {
  background-color: #e1306c;
  color: white;
}
.linkedin {
  background-color: #0a66c2;
  color: white;
}
.facebook {
  background-color: #1877f2;
  color: white;
}

.loading-state,
.error-state {
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
}
</style>